import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, XCircle, Loader, Info } from 'lucide-react';
import VisualizationTab from './VisualizationTab';
import api from '../services/api';
import mlApi from '../services/mlApi';

// Parse flat log array into grouped steps using step markers.
// Markers: '__STEP__:label', '__DONE__', '__ERROR__:message'
const parseSteps = (logs) => {
  const steps = [];
  let current = null;

  const flush = () => {
    if (current && (current.label || current.lines.length > 0)) {
      steps.push({ ...current, lines: [...current.lines] });
    }
  };

  for (const log of logs) {
    if (log.startsWith('__STEP__:')) {
      flush();
      current = { label: log.slice(9), status: 'running', lines: [] };
    } else if (log === '__DONE__') {
      if (current) { current.status = 'completed'; flush(); }
      current = null;
    } else if (log.startsWith('__ERROR__:')) {
      if (current) {
        current.status = 'error';
        current.lines.push(log.slice(10));
        flush();
      } else {
        steps.push({ label: null, status: 'error', lines: [log.slice(10)] });
      }
      current = null;
    } else {
      if (!current) current = { label: null, status: 'info', lines: [] };
      current.lines.push(log);
    }
  }
  flush();
  return steps;
};

const StatusIcon = ({ status }) => {
  if (status === 'completed') return <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />;
  if (status === 'error')     return <XCircle     className="h-4 w-4 text-red-400 flex-shrink-0" />;
  if (status === 'running')   return <Loader      className="h-4 w-4 text-blue-400 flex-shrink-0 animate-spin" />;
  return                             <Info        className="h-4 w-4 text-gray-500 flex-shrink-0" />;
};

const ExecutionLogs = ({ isOpen, onClose, logs, currentDatasetId, nodes, initialActiveTab = 'logs' }) => {
  const [activeTab, setActiveTab] = useState('logs');
  const [dataPreview, setDataPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialActiveTab) setActiveTab(initialActiveTab);
  }, [initialActiveTab, isOpen]);

  useEffect(() => {
    if (isOpen && activeTab === 'logs') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen, activeTab]);

  useEffect(() => {
    if (activeTab === 'data' && currentDatasetId) fetchDataPreview();
  }, [activeTab, currentDatasetId]);

  const fetchDataPreview = async () => {
    try {
      setLoading(true);
      const isMongoId = /^[0-9a-fA-F]{24}$/.test(currentDatasetId);
      const response = isMongoId
        ? await api.get(`/datasets/${currentDatasetId}`)
        : await mlApi.get(`/dataset?dataset_id=${currentDatasetId}`);

      if (response.data?.metadata?.previewData) {
        setDataPreview({
          columns: response.data.metadata.columns,
          rows: response.data.metadata.previewData,
          rowCount: response.data.metadata.rowCount,
        });
      } else if (response.data?.columns && response.data?.previewData) {
        setDataPreview({
          columns: response.data.columns,
          rows: response.data.previewData,
          rowCount: response.data.rowCount,
        });
      } else if (typeof response.data === 'string') {
        const lines = response.data.split('\n');
        if (lines.length > 1) {
          const columns = lines[0].split(',').map(c => c.trim());
          const rows = [];
          for (let i = 1; i < Math.min(11, lines.length); i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(',');
              const row = {};
              columns.forEach((col, idx) => { row[col] = values[idx]?.trim() || ''; });
              rows.push(row);
            }
          }
          setDataPreview({ columns, rows, rowCount: lines.length - 1 });
        }
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        const columns = Object.keys(response.data[0]);
        setDataPreview({ columns, rows: response.data.slice(0, 10), rowCount: response.data.length });
      }
    } catch (error) {
      console.error('Error fetching dataset preview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const steps = parseSteps(logs);
  const hasSteps = steps.some(s => s.label);

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 h-full">
      <div className="bg-gray-900 rounded-xl w-full max-w-4xl max-h-[100vh] flex flex-col border border-gray-800/50 shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800/50">
          <h2 className="text-xl font-semibold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
            Workflow Execution Log
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800/50 rounded-lg transition-colors duration-200">
            <X className="h-5 w-5 text-gray-400 hover:text-white" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800/50 px-6">
          {['logs', 'data', 'viz'].map(tab => {
            const label = tab === 'logs' ? 'Run Log' : tab === 'data' ? 'Data Preview' : 'Visualisation';
            return (
              <button
                key={tab}
                className={`px-6 py-4 font-medium transition-all duration-200 relative ${
                  activeTab === tab ? 'text-cyan-400' : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {label}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-400" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-900/50">

          {/* Run Log Tab */}
          {activeTab === 'logs' && (
            <div>
              {logs.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No run started yet.</p>
              ) : !hasSteps ? (
                /* Plain messages before any step markers */
                <div className="space-y-1">
                  {logs.map((line, i) => (
                    <p key={i} className="text-sm text-gray-400 font-mono">{line}</p>
                  ))}
                </div>
              ) : (
                <div className="relative">
                  {/* Vertical guide line */}
                  <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-700/60" />

                  <div className="space-y-1 pl-6">
                    {steps.map((step, si) => {
                      if (!step.label) {
                        /* Pipeline-level messages (pre/post run) */
                        return step.lines.map((line, li) => (
                          <p key={`info-${si}-${li}`} className="text-xs text-gray-500 font-mono py-0.5">{line}</p>
                        ));
                      }

                      return (
                        <div key={si} className="relative pb-4">
                          {/* Status dot on the line */}
                          <div className="absolute -left-6 top-0.5">
                            <StatusIcon status={step.status} />
                          </div>

                          {/* Step header */}
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-semibold ${
                              step.status === 'completed' ? 'text-emerald-300' :
                              step.status === 'error'     ? 'text-red-300' :
                              step.status === 'running'   ? 'text-blue-300' :
                              'text-gray-300'
                            }`}>
                              {step.label}
                            </span>
                            {step.status === 'completed' && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-400">done</span>
                            )}
                            {step.status === 'error' && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-red-900/40 text-red-400">failed</span>
                            )}
                            {step.status === 'running' && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-400 animate-pulse">running</span>
                            )}
                          </div>

                          {/* Step log lines */}
                          {step.lines.length > 0 && (
                            <div className="ml-1 pl-3 border-l border-gray-700/40 space-y-0.5">
                              {step.lines.map((line, li) => (
                                <p key={li} className={`text-xs font-mono py-0.5 ${
                                  step.status === 'error' ? 'text-red-400' : 'text-gray-400'
                                }`}>
                                  {line}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Data Preview Tab */}
          {activeTab === 'data' && (
            <div className="space-y-4 text-gray-300">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
                </div>
              ) : !currentDatasetId ? (
                <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30">
                  <p className="text-center text-gray-400">No dataset selected</p>
                </div>
              ) : dataPreview ? (
                <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-cyan-400">Dataset Preview</h3>
                    <span className="text-xs text-gray-400">Total: {dataPreview.rowCount} rows</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-800/50 border-b border-gray-700/50">
                          <th className="py-2 px-3 text-left font-medium text-gray-300">#</th>
                          {dataPreview.columns.map(col => (
                            <th key={col} className="py-2 px-3 text-left font-medium text-gray-300">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dataPreview.rows.map((row, i) => (
                          <tr key={i} className="border-b border-gray-800/30 hover:bg-gray-800/20">
                            <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                            {dataPreview.columns.map(col => (
                              <td key={`${i}-${col}`} className="py-2 px-3">{row[col]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30">
                  <p className="text-center text-gray-400">No data preview available</p>
                </div>
              )}
            </div>
          )}

          {/* Visualisation Tab */}
          {activeTab === 'viz' && (
            <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30">
              <VisualizationTab
                datasetId={currentDatasetId}
                featureColumns={nodes.find(n => n.type === 'configureFeatures')?.data?.parameters?.feature_columns || []}
                targetColumn={nodes.find(n => n.type === 'configureFeatures')?.data?.parameters?.target_column}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionLogs;
